import BellIcon from '@heroicons/react/outline/BellIcon'
import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon'
import MenuAlt1Icon from '@heroicons/react/outline/MenuAlt1Icon'
import MenuAlt2Icon from '@heroicons/react/outline/MenuAlt2Icon'
import NewspaperIcon from '@heroicons/react/outline/NewspaperIcon'
import PhotographIcon from '@heroicons/react/outline/PhotographIcon'
import UserCircleIcon from '@heroicons/react/outline/UserCircleIcon'
import UserIcon from '@heroicons/react/outline/UserIcon'
import GridIcon from '@heroicons/react/outline/ViewGridIcon'

import { SpaceListOrderByEnum } from '@tribeplatform/gql-client/types'
import type { I18nShape } from '@tribeplatform/react-components/i18n'

import type { StaticField } from '../Views/types'
import { StaticFieldTypes } from '../Views/types'
import type { SpacesListBlockProps, SpacesListBlockView } from './types'

export const genericViewFactory: (
  intl: I18nShape,
) => SpacesListBlockView = intl => ({
  id: '',
  collectionId: null,
  name: intl.$t({
    defaultMessage: 'Default',
    id: 'Generics.Default',
  }),
  filterBy: 'collectionId',
  sort: SpaceListOrderByEnum.CREATED_AT,
  reverseSort: true,
  style: 'list',
  gallerySize: 'medium',
  showDivider: true,
  limit: 10,
  fields: [],
  showMore: false,
})

export const staticFieldsFactory: (intl: I18nShape) => StaticField[] = intl => [
  {
    id: 'galleryBanner',
    label: intl.$t({
      defaultMessage: 'Banner',
      id: 'Generics.Banner',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel'],
    type: StaticFieldTypes.banner,
    settings: {
      bannerStyle: 'banner',
      bannerSource: 'banner',
      aspectRatio: 'aspect-video',
    },
    isStatic: true,
    icon: <PhotographIcon />,
  },
  {
    id: 'cardBanner',
    label: intl.$t({
      defaultMessage: 'Banner',
      id: 'Generics.Banner',
    }),
    enabled: false,
    locked: true,
    styles: ['card'],
    type: StaticFieldTypes.banner,
    settings: {
      bannerStyle: 'banner',
      bannerSource: 'banner',
      aspectRatio: 'aspect-[4/1]',
    },
    isStatic: true,
    icon: <PhotographIcon />,
    default: '/images/banners/banner-placeholder.jpg',
  },
  {
    id: 'listImage',
    label: intl.$t({
      defaultMessage: 'Image',
      id: 'Generics.Image',
    }),
    enabled: true,
    locked: false,
    styles: ['list'],
    type: StaticFieldTypes.avatar,
    settings: {
      avatarSize: 'lg',
      bannerSource: 'image',
    },
    isStatic: true,
    icon: <UserCircleIcon />,
  },
  {
    id: 'listSpaceSummary',
    label: intl.$t({
      defaultMessage: '{SPACE_CC} summary',
      id: 'SpacesListBlocks.FieldLabels.SpaceSummary',
    }),
    enabled: true,
    locked: false,
    styles: ['list'],
    settings: {
      stretchX: true,
    },
    isStatic: true,
    icon: <NewspaperIcon />,
    fields: [
      {
        id: 'listSpaceName',
        label: intl.$t({
          defaultMessage: 'Name',
          id: 'Generics.Name.Noun',
        }),
        enabled: true,
        locked: false,
        type: StaticFieldTypes.title,
        settings: {
          titleSize: 'md',
        },
        isStatic: true,
        icon: <MenuAlt2Icon />,
        default: 'Lorem sunt excepteur aliquip',
      },
      {
        id: 'listSpaceDescription',
        label: intl.$t({
          defaultMessage: 'Description',
          id: 'Generics.Description',
        }),
        enabled: true,
        locked: false,
        settings: {},
        isStatic: true,
        icon: <MenuAlt1Icon />,
        default:
          'Commodo magna esse ea in in. Minim occaecat aute veniam pariatur laboris veniam.',
      },
    ],
  },
  {
    id: 'gallerySpaceName',
    label: intl.$t({
      defaultMessage: 'Name',
      id: 'Generics.Name.Noun',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel', 'card'],
    type: StaticFieldTypes.title,
    settings: {
      fullWidth: true,
      titleSize: 'md',
    },
    isStatic: true,
    icon: <MenuAlt2Icon />,
    default: 'Lorem sunt excepteur aliquip',
  },
  {
    id: 'gallerySpaceDescription',
    label: intl.$t({
      defaultMessage: 'Description',
      id: 'Generics.Description',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel'],
    settings: {
      fullWidth: true,
    },
    isStatic: true,
    icon: <MenuAlt1Icon />,
    default:
      'Commodo magna esse ea in in. Minim occaecat aute veniam pariatur laboris veniam.',
  },
  {
    id: 'cardSpaceDescription',
    label: intl.$t({
      defaultMessage: 'Description',
      id: 'Generics.Description',
    }),
    enabled: true,
    locked: false,
    styles: ['card'],
    settings: {
      fullWidth: true,
      displayStyle: 'truncated',
    },
    isStatic: true,
    icon: <MenuAlt1Icon />,
    default:
      'Commodo magna esse ea in in. Minim occaecat aute veniam pariatur laboris veniam.',
  },
  {
    id: 'membersCount',
    label: intl.$t({
      defaultMessage: 'Members count',
      id: 'SpacesListBlocks.FieldLabels.MembersCount',
    }),
    type: StaticFieldTypes.info,
    enabled: false,
    locked: false,
    settings: { buttonVariant: 'iconText' },
    isStatic: true,
    icon: <UserIcon />,
  },
  {
    id: 'postsCount',
    label: intl.$t({
      defaultMessage: 'Posts count',
      id: 'SpacesListBlocks.FieldLabels.PostsCount',
    }),
    type: StaticFieldTypes.info,
    enabled: false,
    locked: false,
    settings: { buttonVariant: 'iconText' },
    isStatic: true,
    icon: <DocumentTextIcon />,
  },
  {
    id: 'joinButton',
    label: intl.$t({
      defaultMessage: 'Join button',
      id: 'SpacesListBlocks.FieldLabels.JoinButton',
    }),
    type: StaticFieldTypes.button,
    enabled: false,
    locked: false,
    settings: { buttonSize: 'lg' },
    isStatic: true,
    icon: <BellIcon />,
  },
]

export const SpacesListConfig: (intl: I18nShape) => {
  defaultProps: SpacesListBlockProps
  displayName: string
  removable: boolean
  Icon: JSX.Element
} = ({ $t }) => ({
  defaultProps: {
    views: [],
    title: '',
    description: '',
    linkText: '',
    linkUrl: '',
  },
  displayName: $t({
    defaultMessage: 'Spaces',
    id: 'Blocks.SpacesList.DisplayName',
  }),
  removable: true,
  Icon: <GridIcon />,
})
